/**
 * The floating view-mode widget, pinned to the stage's top-right corner and
 * hovering over the content (2D plan or 3D canvas).
 *
 * Row 1 is the view segmented control, then the view menu, the export menu,
 * and the settings gear at its end (the per-machine preferences, see
 * SettingsMenu). In 3D mode the navigation tools row follows (the Rotate /
 * Pan drag tool) in the same floating rectangle. The tools row reads and
 * drives the shell's camera controls (`useView3D`); the view cube beside the
 * canvas does Home.
 * The data-view / data-act / data-testid attributes are kept as stable E2E anchors.
 */

import { css } from '@emotion/react';
import type { ExportFormat, FloorPlanEditorMode } from '@kaiizen/kai-floor-plan-editor-core';
import { fBody, ink, midtone, monoLabel, paper, widgetShadow } from '../styles';
import { useView3D, VIEW3D_TOOLS, type View3DTool } from '../view3d';
import { RoomExportMenu } from './ExportMenu';
import { SettingsMenu } from './SettingsMenu';
import { ViewMenu } from './ViewMenu';

export const VIEW_OVERLAY_STYLE = css`
  label: VIEW_OVERLAY_STYLE;
  position: absolute; top: 14px; right: 14px; z-index: 30;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${ink};
  box-shadow: ${widgetShadow};
  backdrop-filter: blur(6px);
  user-select: none;
  font-size: 11px; line-height: 1.5; color: ${ink};
`;

/** The top row: the view segments filling the width, the settings gear at the end. */
export const TOP_ROW_STYLE = css`
  label: TOP_ROW_STYLE;
  display: flex; align-items: stretch;
`;

export const SEG_ROW_STYLE = css`
  label: SEG_ROW_STYLE;
  flex: 1; display: grid; grid-auto-flow: column; grid-auto-columns: 1fr;
`;

export const SEG_STYLE = css`
  label: SEG_STYLE;
  ${monoLabel}
  padding: 9px 10px; border: 0; font-size: 10px; letter-spacing: 0.06em;
  background: transparent; color: ${midtone};
  cursor: pointer; transition: background .12s, color .12s;
  &:not(:first-of-type) { border-left: 1px solid ${ink}; }
  &:hover { color: ${ink}; background: ${paper}; }
`;

export const SEG_ACTIVE_STYLE = css`
  label: SEG_ACTIVE_STYLE;
  background: ${ink}; color: ${paper};
  &:hover { color: ${paper}; background: ${ink}; }
`;

/** A second line in the same rectangle (the 3D tools). */
export const SUB_ROW_STYLE = css`
  label: SUB_ROW_STYLE;
  border-top: 1px solid ${ink};
  padding: 8px 10px;
`;

/** Small two-option pill (Rotate/Pan). */
const PILL_STYLE = css`
  label: PILL_STYLE;
  display: inline-flex; border: 1px solid ${ink};
`;

const PILL_BUTTON_STYLE = css`
  label: PILL_BUTTON_STYLE;
  border: 0; cursor: pointer; font: 600 11px ${fBody};
  padding: 4px 14px; color: ${midtone}; background: transparent;
`;

const PILL_BUTTON_ACTIVE_STYLE = css`
  label: PILL_BUTTON_ACTIVE_STYLE;
  background: ${ink}; color: ${paper};
`;

const VIEW_SEGMENTS: [FloorPlanEditorMode, string][] = [
  ['2d', '2D Plan'],
  ['3d', '3D View'],
];

const VIEW3D_TOOLS_ROW_STYLE = css`
  label: VIEW3D_TOOLS_ROW_STYLE;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
`;

const TOOL_TITLES: Record<View3DTool, string> = {
  rotate: 'Rotate: drag or scroll orbits the model (hold Shift to pan)',
  pan: 'Pan: drag or scroll slides the plan (hold Shift to rotate)',
};

/**
 * The 3D navigation tools, driving the shell's camera controls: the Rotate /
 * Pan drag tool (shift swaps it for one gesture). Shared with the assembler's
 * overlay.
 */
export function View3DToolsRow(): React.JSX.Element {
  const { tool, setTool } = useView3D();
  return (
    <div css={VIEW3D_TOOLS_ROW_STYLE} data-testid='view-3d-tools'>
      <div css={PILL_STYLE} role='group' aria-label='Drag tool'>
        {VIEW3D_TOOLS.map(option => (
          <button
            key={option.id}
            css={[PILL_BUTTON_STYLE, tool === option.id && PILL_BUTTON_ACTIVE_STYLE]}
            data-tool={option.id}
            aria-pressed={tool === option.id}
            title={TOOL_TITLES[option.id]}
            onClick={() => {
              if (tool !== option.id) setTool(option.id);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface ViewOverlayProps {
  mode: FloorPlanEditorMode;
  onMode: (mode: FloorPlanEditorMode) => void;
  /** The export menu's file downloads: the room as a 2D plan (DXF/SVG) or a 3D model (STEP/STL). */
  onExport: (format: ExportFormat) => void;
}

export function ViewOverlay(p: ViewOverlayProps): React.JSX.Element {
  return (
    <div css={VIEW_OVERLAY_STYLE}>
      <div css={TOP_ROW_STYLE}>
        <div css={SEG_ROW_STYLE} data-testid='view-toggle' role='group' aria-label='View mode'>
          {VIEW_SEGMENTS.map(([mode, label]) => (
            <button
              key={mode}
              css={[SEG_STYLE, p.mode === mode && SEG_ACTIVE_STYLE]}
              data-view={mode}
              aria-pressed={p.mode === mode}
              onClick={() => {
                if (p.mode !== mode) p.onMode(mode);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <ViewMenu />
        <RoomExportMenu onExport={p.onExport} />
        <SettingsMenu />
      </div>

      {p.mode === '3d' && (
        <div css={SUB_ROW_STYLE}>
          <View3DToolsRow />
        </div>
      )}
    </div>
  );
}
